import React, { createContext, useContext, ReactNode } from 'react';
import { BundleOption } from '../../../api/shopify/types';
import { ScoredAlgoliaHit } from '@jbhi-fi/apis/src/algolia/ProductClient';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useSuggestedProducts } from '@jbhi-fi/components/src/hooks/useSuggestedProducts';
import { getAnalyticsTags } from '../../../utils/algolia/search-insights';
import { AlgoliaProductHit } from '../../../utils/types';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { fetchCurrentBundleOffering } from '../../../snippets/fetchCurrentBundleOffering';
import { SmartModuleSettings } from '../../../../global';

export const EMPTY_CART_SMARTMODULE_SETTINGS: SmartModuleSettings = {
  isEnabled: true,
  type: 'recentlyViewed',
  title: 'Pick up where you left off',
  googleRecModelType: '',
  isAlternativesEnabled: false,
  productCount: 6,
  alternativesCount: 6,
};

export type SmartModuleData = {
  frequentlyBoughtTogetherAlgoliaData?: Array<ScoredAlgoliaHit>;
  productBundleAlgoliaData?: Array<ScoredAlgoliaHit>;
  recentlyViewedAlgoliaData?: Array<ScoredAlgoliaHit>;
  recommendedForYouAlgoliaData?: Array<ScoredAlgoliaHit>;
};

const filterAvailableBundleItems = (products: Array<AlgoliaProductHit>) => {
  if (products?.length) {
    return products.filter(product => product.button === 'Buy' || product.button === 'PreOrder');
  }
  return [];
};

type SmartModuleContextOptions = {
  googleAcceptedDeviationPercentage: number;
  requestedProductsCount?: number;
  smartModuleSettings?: Array<SmartModuleSettings>;
  isEnhancedGoogleRecommendationsEnabled?: boolean;
  preservedProductCategories?: Array<number>;
};

type SmartModuleContextProps = {
  smartModulesData: SmartModuleData;
  isLoading: boolean;
  isEnhancedGoogleRecommendationsEnabled?: boolean;
};

export type SourceProduct = {
  sku: string;
  productGroup: string | number;
  optimizelyBundleOptions?: Array<BundleOption>;
  bundleOptions?: Array<BundleOption>;
};

type SmartModuleProviderProps = {
  sourceProducts?: Array<SourceProduct>;
  options?: SmartModuleContextOptions;
  children: ReactNode;
};

const SmartModuleContext = createContext<SmartModuleContextProps | undefined>(undefined);

const SmartModuleProvider = ({ sourceProducts, options, children }: SmartModuleProviderProps): JSX.Element => {
  const { cart } = useCartContext();
  const sourceProductSkus = sourceProducts?.map(product => product.sku);
  const excludedSkus = cart?.items.map(item => item.sku) ?? [];

  const smartModuleSettings = options?.smartModuleSettings;
  const frequentlyBoughtTogetherSettings = smartModuleSettings?.find(
    option => option?.type === 'frequentlyBoughtTogether'
  );
  const recentlyViewedSettings = smartModuleSettings?.find(option => option?.type === 'recentlyViewed');
  const recommendedForYouSettings = smartModuleSettings?.find(option => option?.type === 'recommendedForYou');

  const { products: recentlyViewedProducts, isLoading: isRecentlyViewedLoading } = useProducts({
    isRecentlyViewed: recentlyViewedSettings?.isEnabled,
    isEnabled: recentlyViewedSettings?.isEnabled,
  });

  /**
   * PRODUCT BUNDLE DATA
   */
  let bundleSuggestion: BundleOption | undefined;
  if (sourceProducts?.length === 1) {
    const currentProduct = sourceProducts[0];
    if (currentProduct?.sku) {
      bundleSuggestion = fetchCurrentBundleOffering(
        currentProduct.sku,
        currentProduct.productGroup,
        currentProduct.optimizelyBundleOptions || [],
        currentProduct.bundleOptions || []
      );
    }
  }

  const { products: bundleSuggestions, isLoading: isBundleSuggestionLoading } = useProducts({
    productSkus: [...(sourceProductSkus || []), ...(bundleSuggestion?.suggestedSkus.map(sku => sku.toString()) || [])],
    isEnabled: !!bundleSuggestion,
  });

  const { suggestions: frequentlyBoughtTogetherSuggestions, isLoading: isFrequentlyBoughtTogetherProductsLoading } =
    useSuggestedProducts({
      products: sourceProducts?.map(product => ({
        sku: product.sku,
        category_id: Number(product.productGroup),
      })),
      googleRecNumProducts: options?.requestedProductsCount || 12,
      googleRecModel: frequentlyBoughtTogetherSettings?.googleRecModelType || 'FBT',
      googleRecAcceptedDeviationPercentage: options?.googleAcceptedDeviationPercentage,
      excludedSkus: excludedSkus,
      isEnabled: frequentlyBoughtTogetherSettings?.isEnabled ?? false,
      analyticsTags: getAnalyticsTags('recai'),
      isAttachSuggestionEnabled: false,
      shouldEnhanceSuggestions: options?.isEnhancedGoogleRecommendationsEnabled,
      protectedProductCategories: options?.preservedProductCategories,
      shouldKeepPreviousData: true,
    });

  const { suggestions: recommendedForYouSuggestions, isLoading: isRecommendedForYouProductsLoading } =
    useSuggestedProducts({
      products: sourceProducts?.map(product => ({
        sku: product.sku,
        category_id: Number(product.productGroup),
      })),
      googleRecNumProducts: options?.requestedProductsCount || 12,
      googleRecModel: recommendedForYouSettings?.googleRecModelType || 'RFY',
      googleRecAcceptedDeviationPercentage: options?.googleAcceptedDeviationPercentage,
      excludedSkus: excludedSkus,
      isEnabled: recommendedForYouSettings?.isEnabled ?? false,
      analyticsTags: getAnalyticsTags('recai'),
      isAttachSuggestionEnabled: false,
      shouldEnhanceSuggestions: options?.isEnhancedGoogleRecommendationsEnabled,
      protectedProductCategories: options?.preservedProductCategories,
      shouldKeepPreviousData: true,
    });

  const isLoading =
    isFrequentlyBoughtTogetherProductsLoading ||
    isRecommendedForYouProductsLoading ||
    isRecentlyViewedLoading ||
    isBundleSuggestionLoading;

  const productBundleAlgoliaData = filterAvailableBundleItems(bundleSuggestions || []);

  const smartModulesData = {
    productBundleAlgoliaData,
    recentlyViewedAlgoliaData: recentlyViewedProducts?.filter(p => !sourceProductSkus?.includes(p.sku)) || [],
    frequentlyBoughtTogetherAlgoliaData: frequentlyBoughtTogetherSuggestions,
    recommendedForYouAlgoliaData: recommendedForYouSuggestions,
  };

  return (
    <SmartModuleContext.Provider
      value={{
        smartModulesData,
        isLoading,
        isEnhancedGoogleRecommendationsEnabled: options?.isEnhancedGoogleRecommendationsEnabled,
      }}
    >
      {children}
    </SmartModuleContext.Provider>
  );
};

const useSmartModuleContext = (): SmartModuleContextProps => {
  const context = useContext(SmartModuleContext);

  if (!context) {
    throw new Error('useSmartModule must be used within a SmartModuleProvider');
  }

  return context;
};

export { SmartModuleProvider, useSmartModuleContext };
